import type { PickBody, PickResult } from "../pick/api";
import type { ActivityScans, ScanInput } from "../scans/api";
import type { ShiftStats, ShiftStatus, StaffConfig } from "../states/api";

// Мок без сервера — только для разработки: `npm run dev:merchant-employee`, открыть с `?mock`.
// Состояния и исходы — как в правиле фейковой 1С; сканы «приходят» сами по одному раз в 15 секунд
// после входа в консультацию, как будто продавец сканирует бирки тегом на главной.
export const MOCK = import.meta.env.DEV && new URLSearchParams(window.location.search).has("mock");

const delay = (ms = 250) => new Promise<void>((resolve) => setTimeout(resolve, ms));
// Копия без ссылок на состояние мока; даты остаются датами, как после десериализации DTO.
const asJson = <T>(value: unknown): T =>
  JSON.parse(JSON.stringify(value), (_key, item) =>
    typeof item === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(item) ? new Date(item) : item,
  ) as T;

const reasonsSale = [
  { id: "rr-expensive", name: "Дорого" },
  { id: "rr-no-size", name: "Нет размера", questionnaireId: "q-size" },
  { id: "rr-no-model", name: "Нет модели / не нашёл", questionnaireId: "q-item" },
  { id: "rr-thinking", name: "Ушёл подумать" },
  { id: "rr-other", name: "Другое" },
];
const item = (id: string, name: string, kind: string, extra: Record<string, unknown>) => ({
  id,
  name,
  type: "item",
  kind,
  refusalReasons: [],
  commentRequired: false,
  ...extra,
});
const config = {
  askCustomerOnEnter: new URLSearchParams(window.location.search).has("ask"),
  questionnaires: [
    {
      id: "q-item",
      name: "Что искал",
      title: "Что спрашивал клиент — заявка в товарную матрицу",
      // У шаблона есть схема подбора: по этой анкете работает «Подобрать».
      pick: true,
      fields: [
        {
          id: "kind",
          name: "Вид изделия",
          type: "select",
          required: true,
          options: [
            { id: "k-ring", name: "Кольцо" },
            { id: "k-chain", name: "Цепь" },
            { id: "k-ear", name: "Серьги" },
          ],
        },
        {
          id: "metal",
          name: "Металл",
          type: "select",
          required: false,
          multiple: true,
          options: [
            { id: "m-585", name: "Золото 585" },
            { id: "m-750", name: "Золото 750" },
            { id: "m-925", name: "Серебро 925" },
          ],
        },
        {
          id: "insert",
          name: "Вставка",
          type: "select",
          required: false,
          multiple: true,
          options: [
            { id: "i-none", name: "Без вставки" },
            { id: "i-diamond", name: "Бриллиант" },
            { id: "i-cz", name: "Фианит" },
            { id: "i-sapphire", name: "Сапфир" },
          ],
        },
        { id: "price", name: "Ориентир цены", type: "number", required: false, unit: "₽" },
        { id: "note", name: "Комментарий", type: "text", required: false, placeholder: "Что ещё важно клиенту" },
      ],
    },
    {
      id: "q-size",
      name: "Нужный размер",
      title: "Какой размер искал клиент",
      fields: [
        {
          id: "kind",
          name: "Вид изделия",
          type: "select",
          required: true,
          options: [
            { id: "k-ring", name: "Кольцо" },
            { id: "k-brace", name: "Браслет" },
          ],
        },
        { id: "size", name: "Размер", type: "text", required: true },
      ],
    },
  ],
  states: [
    { id: "st-hall", name: "В зале", isBase: true, icon: "store", color: "#155E4D", sort: 1, outcomes: [] },
    {
      id: "st-client",
      name: "С клиентом",
      isBase: false,
      icon: "user",
      color: "#A57A2A",
      sort: 2,
      outcomes: [
        item("oc-sale", "Продажа", "deal", {
          hint: "связать с чеком ККМ",
          icon: "receipt",
          document: { type: "ЧекККМ", title: "Выберите чек" },
        }),
        {
          id: "gr-other-deal",
          name: "Другая сделка",
          type: "group",
          children: [
            item("oc-buyback", "Скупка", "deal", {
              hint: "связать с документом скупки",
              icon: "exchange",
              document: { type: "Скупка", title: "Выберите документ скупки" },
            }),
            item("oc-pawn", "Залог / перезалог", "deal", {
              hint: "залоговый билет",
              icon: "lock",
              document: { type: "ЗалоговыйБилет", title: "Выберите залоговый билет" },
            }),
            item("oc-interest", "Оплата процентов", "deal", {
              hint: "документ оплаты",
              icon: "percent",
              document: { type: "ОплатаПроцентов", title: "Выберите документ оплаты" },
            }),
          ],
        },
        {
          id: "gr-no-deal",
          name: "Сделки не было — консультация",
          type: "group",
          children: [
            item("oc-refuse-sale", "По продаже — отказ", "refusal", { icon: "diamond", refusalReasons: reasonsSale }),
            item("oc-refuse-pawn", "По залогу — отказ", "refusal", {
              icon: "lock",
              refusalReasons: [
                { id: "rr-low-sum", name: "Не устроила сумма залога" },
                { id: "rr-no-passport", name: "Нет паспорта" },
                { id: "rr-other", name: "Другое" },
              ],
            }),
          ],
        },
      ],
    },
    { id: "st-break", name: "Перерыв", isBase: false, icon: "coffee", sort: 3, outcomes: [] },
    { id: "st-display", name: "Выкладка", isBase: false, icon: "layers", sort: 4, outcomes: [] },
    { id: "st-receiving", name: "Приёмка", isBase: false, icon: "box", sort: 5, outcomes: [] },
  ],
};

const ITEMS = [
  { title: "Кольцо Au585 К-1102", subtitle: "17 р-р · 34 900 ₽" },
  { title: "Цепь бисмарк 55 см", subtitle: "Au585 · 58 300 ₽" },
  { title: "Кольцо Au585 К-0987", subtitle: "16,5 р-р · 27 100 ₽" },
];

let seq = 0;
let shift: { id: string; openedAt: Date } | null = null;
let current: { activityId: string; stateId: string; stateName: string; isBase: boolean; startedAt: Date } | null = null;
// Простые объекты, а не DTO: наружу уходит копия через asJson, как после десериализации.
let scans: { customer?: object; items: object[] } = { items: [] };

const stateById = (id: string) => config.states.find((state) => state.id === id)!;

function enter(stateId: string) {
  const state = stateById(stateId);
  current = { activityId: `a${++seq}`, stateId, stateName: state.name, isBase: state.isBase, startedAt: new Date() };
  scans = { items: [] };
}

const status = () => asJson<ShiftStatus>({ shift, current });

// Стартовая сцена для скриншотов: `?mock=client` — смена открыта и идёт консультация,
// `?mock=customer` — ещё и клиент уже отсканирован, `?mock=outcomes` — то же плюс пара бирок.
const scene = new URLSearchParams(window.location.search).get("mock");
if (scene && scene !== "hall") {
  shift = { id: `s${++seq}`, openedAt: new Date(Date.now() - 2 * 3600e3) };
  enter("st-client");
  current!.startedAt = new Date(Date.now() - 372e3);
  if (scene === "customer" || scene === "outcomes") {
    scans.customer = {
      name: "Иванова Ирина",
      cardMasked: "••• 4512",
      phoneMasked: "+7 912 ••• 67-89",
      bonuses: 1240,
      found: true,
    };
  }
} else if (scene === "hall") {
  shift = { id: `s${++seq}`, openedAt: new Date(Date.now() - 2 * 3600e3) };
  enter("st-hall");
}

export const mock = {
  getConfig: async () => {
    await delay();
    return asJson<StaffConfig>(config);
  },
  getShift: async () => {
    await delay();
    return status();
  },
  openShift: async () => {
    await delay();
    shift = { id: `s${++seq}`, openedAt: new Date() };
    enter("st-hall");
    return status();
  },
  enterState: async (stateId: string) => {
    await delay();
    enter(stateId);
    return status();
  },
  finishState: async () => {
    await delay();
    enter("st-hall");
    return status();
  },
  closeShift: async () => {
    await delay();
    shift = null;
    current = null;
  },
  getStats: async () => {
    await delay();
    return asJson<ShiftStats>({
      shiftOpenedAt: shift?.openedAt,
      onShiftSeconds: shift ? Math.floor((Date.now() - shift.openedAt.getTime()) / 1000) : 0,
      byState: [{ stateId: "st-client", stateName: "С клиентом", seconds: 1860, count: 4 }],
      consultations: 4,
      outcomes: { deal: 2, refusal: 2, neutral: 0 },
      conversion: 0.5,
      dealsAmount: 62000,
      deals: [{ outcomeName: "Продажа", title: "Чек ККМ №4821", amount: 34900, finishedAt: new Date() }],
      refusalReasons: [{ reasonName: "Дорого", count: 2 }],
    });
  },
  getDocuments: async () => {
    await delay();
    return asJson<never[]>([
      {
        id: "d-1",
        type: "ЧекККМ",
        title: "Чек ККМ №4821",
        date: new Date(),
        amount: 34900,
        items: [{ name: "Кольцо Au585 с бриллиантом", article: "К-1102", quantity: 1 }],
        suggested: true,
      },
      {
        id: "d-2",
        type: "ЧекККМ",
        title: "Чек ККМ №4820",
        date: new Date(Date.now() - 3600e3),
        amount: 12400,
        items: [],
      },
    ]);
  },
  getScans: async () => {
    await delay();
    // Бирки «сканируются» сами: по одной каждые 15 секунд консультации.
    if (current && !current.isBase) {
      const count = Math.min(ITEMS.length, Math.floor((Date.now() - current.startedAt.getTime()) / 15000));
      while (scans.items.length < count) {
        const next = ITEMS[scans.items.length];
        scans.items.push({ scanId: `sc${++seq}`, ...next, scannedAt: new Date().toISOString() });
      }
    }
    return asJson<ActivityScans>(scans);
  },
  sendScan: async (body: ScanInput) => {
    await delay(600);
    if ("phone" in body) {
      const masked = `+7 ${body.phone.slice(1, 4)} ••• ${body.phone.slice(7, 9)}-${body.phone.slice(9)}`;
      scans.customer = body.phone.endsWith("0000")
        ? { phoneMasked: masked, found: false }
        : { name: "Иванова Ирина", cardMasked: "••• 4512", phoneMasked: masked, bonuses: 1240, found: true };
    } else {
      scans.items.push({ scanId: `sc${++seq}`, ...ITEMS[scans.items.length % ITEMS.length], scannedAt: new Date() });
    }
    return asJson<ActivityScans>(scans);
  },
  // Подбор: HTML и цифры, как их отдаст 1С по схеме подбора. Ориентир цены ниже 10 000 — пусто по салону.
  pick: async (body: PickBody) => {
    await delay(900);
    const price = body.answers.find((answer) => answer.fieldId === "price")?.value;
    const cheap = typeof price === "number" && price < 10000;
    const network = body.scope === "network";
    const here = cheap ? [] : PICK_ITEMS.slice(0, 3).map((item, index) => ({ ...item, here: PICK_SIZES[index] }));
    const rows = network ? [...here, ...PICK_ITEMS.slice(3).map((item) => ({ ...item, here: null }))] : here;
    const summary = {
      total: rows.length,
      shown: rows.length,
      inShop: here.length,
      inNetwork: !network && !rows.length ? 2 : undefined,
      truncated: false,
    };
    return asJson<PickResult>({ html: pickHtml(rows, network ? "По всей сети" : "В этом салоне"), summary });
  },
};

const PICK_ITEMS = [
  { name: "Кольцо Au585 с бриллиантом", article: "К-1184 · 2,81 г", amount: 21600, elsewhere: "" },
  { name: "Кольцо Au585 с бриллиантом", article: "К-1191 · 2,73 г", amount: 19400, elsewhere: "" },
  { name: "Кольцо Au585 с фианитом", article: "К-1352 · 2,60 г", amount: 9800, elsewhere: "" },
  { name: "Кольцо Au585 с бриллиантом", article: "К-1109 · 3,10 г", amount: 41200, elsewhere: "Северный 17,5" },
  { name: "Кольцо Au585 с сапфиром", article: "К-0987 · 2,95 г", amount: 27100, elsewhere: "Люберцы 16" },
];
const PICK_SIZES = ["16,5 · 17", "17 · 17,5", "18 · 19"];

// В стиле карточки сканера 1С.
function pickHtml(rows: ((typeof PICK_ITEMS)[number] & { here: string | null })[], caption: string) {
  const body = rows.length
    ? rows
        .map((row) => {
          const badge = row.here
            ? `<span class="b h">здесь: ${row.here}</span>`
            : `<span class="b n">привезти: ${row.elsewhere}</span>`;
          return (
            `<div class="r"><div><b>${row.name}</b><br><small>${row.article}</small><br>${badge}</div>` +
            `<div class="p">${row.amount.toLocaleString("ru-RU")} ₽</div></div>`
          );
        })
        .join("")
    : `<p class="e">В этом салоне ничего нет</p>`;
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<style>html{-webkit-text-size-adjust:100%}body{margin:0}.jl{font-family:-apple-system,Roboto,Arial,sans-serif;` +
    `font-size:17px;line-height:1.35;padding:16px;color:#1a1a1a}.c{color:#666;font-size:14px;margin:0 0 8px}` +
    `.r{display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-bottom:1px solid #e3e5ea}` +
    `.p{white-space:nowrap;font-weight:700}.b{display:inline-block;margin-top:4px;border-radius:6px;padding:1px 6px;` +
    `font-size:12px;font-weight:700}.h{background:#e5efe9;color:#2f5f4f}.n{background:#f6ecd6;color:#7a5a16}` +
    `.e{color:#666}small{color:#666}</style></head>` +
    `<body><div class="jl"><p class="c">${caption}</p>${body}</div></body></html>`
  );
}
